import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import html2canvas from "html2canvas";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Camera, Upload, Globe, Clock, Type, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hardcoded PIN
const CORRECT_PIN = "0415";

// Timezone configurations
const TIMEZONES = [
  { value: "Asia/Singapore", label: "Singapore (GMT+8)", offset: 8 },
  { value: "Asia/Tokyo", label: "Tokyo (GMT+9)", offset: 9 },
  { value: "Asia/Hong_Kong", label: "Hong Kong (GMT+8)", offset: 8 },
  { value: "Asia/Shanghai", label: "Shanghai (GMT+8)", offset: 8 },
  { value: "Asia/Seoul", label: "Seoul (GMT+9)", offset: 9 },
  { value: "Asia/Bangkok", label: "Bangkok (GMT+7)", offset: 7 },
  { value: "Asia/Jakarta", label: "Jakarta (GMT+7)", offset: 7 },
  { value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur (GMT+8)", offset: 8 },
  { value: "Asia/Manila", label: "Manila (GMT+8)", offset: 8 },
  { value: "Australia/Sydney", label: "Sydney (GMT+11)", offset: 11 },
  { value: "Australia/Melbourne", label: "Melbourne (GMT+11)", offset: 11 },
  { value: "Europe/London", label: "London (GMT+0)", offset: 0 },
  { value: "Europe/Paris", label: "Paris (GMT+1)", offset: 1 },
  { value: "America/New_York", label: "New York (GMT-5)", offset: -5 },
  { value: "America/Los_Angeles", label: "Los Angeles (GMT-8)", offset: -8 },
  { value: "UTC", label: "UTC (GMT+0)", offset: 0 },
];

// PIN Login Screen Component
const PinLogin = ({ onSuccess }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  const handlePinChange = (value) => {
    setPin(value);
    setError(false);
    
    if (value.length === 4) {
      if (value === CORRECT_PIN) {
        onSuccess();
      } else {
        setError(true);
        setTimeout(() => {
          setPin("");
          setError(false);
        }, 1000);
      }
    }
  };

  return (
    <div className="pin-screen">
      <div className="pin-container">
        <div className="pin-logo">
          <img src="/racetime-logo.png" alt="Racetime.sg" className="pin-logo-img" />
        </div>
        <div className="pin-title">
          <Lock size={24} />
          <span>ENTER PIN</span>
        </div>
        <div className={`pin-input-wrapper ${error ? 'pin-error' : ''}`}>
          <InputOTP
            maxLength={4}
            value={pin}
            onChange={handlePinChange}
            data-testid="pin-input"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="pin-slot" />
              <InputOTPSlot index={1} className="pin-slot" />
              <InputOTPSlot index={2} className="pin-slot" />
              <InputOTPSlot index={3} className="pin-slot" />
            </InputOTPGroup>
          </InputOTP>
        </div>
        {error && <div className="pin-error-msg">Incorrect PIN</div>}
        <div className="pin-footer">racetime.sg | Race Timing Solutions</div>
        <div className="pin-version">Last updated 24 Feb 2026 v3</div>
      </div>
    </div>
  );
};

// Main Clock Component
const EpochClock = () => {
  const [epochTime, setEpochTime] = useState(Math.floor(Date.now() / 1000));
  const [timezone, setTimezone] = useState("Asia/Singapore");
  const [freeText, setFreeText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const captureRef = useRef(null);

  // Update epoch time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setEpochTime(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get just the time portion for large display
  const getTimeOnly = useCallback(() => {
    const date = new Date(epochTime * 1000);
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  }, [epochTime, timezone]);

  // Get date portion
  const getDateOnly = useCallback(() => {
    const date = new Date(epochTime * 1000);
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  }, [epochTime, timezone]);

  // Handle screenshot capture and upload
  const handleScreenshot = async () => {
    if (!captureRef.current) return;

    setIsUploading(true);
    toast.info("Capturing screenshot...");

    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: "#09090b",
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Scale to 800px width proportionally
      const targetWidth = 800;
      const scaleFactor = targetWidth / canvas.width;
      const targetHeight = Math.round(canvas.height * scaleFactor);

      const scaledCanvas = document.createElement('canvas');
      scaledCanvas.width = targetWidth;
      scaledCanvas.height = targetHeight;
      const ctx = scaledCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);

      const imageData = scaledCanvas.toDataURL("image/png");
      const notesForFilename = freeText.trim().replace(/ /g, '_') || 'NOTEXT';
      const filename = `${epochTime}_${notesForFilename}.png`;

      toast.info("Uploading to FTP...");

      const response = await axios.post(`${API}/upload-screenshot`, {
        image_data: imageData,
        filename: filename,
      });

      if (response.data.success) {
        toast.success(`Uploaded: ${response.data.remote_filename}`);
      } else {
        toast.error("Upload failed");
      }
    } catch (error) {
      console.error("Screenshot error:", error);
      toast.error(
        error.response?.data?.detail || "Failed to capture/upload screenshot"
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="app-container">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#18181b",
            color: "#fafafa",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      />

      {/* Main capture area */}
      <div ref={captureRef} className="capture-area" data-testid="capture-area">
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <div className="header-brand">
              <img src="/racetime-logo.png" alt="Racetime.sg" className="header-logo" />
            </div>
            <div className="header-subtitle">EPOCH CLOCK</div>
          </div>
          <Button
            onClick={handleScreenshot}
            disabled={isUploading}
            className="screenshot-btn"
            data-testid="screenshot-button"
          >
            {isUploading ? (
              <>
                <Upload className="btn-icon animate-pulse" />
                <span>UPLOADING...</span>
              </>
            ) : (
              <>
                <Camera className="btn-icon" />
                <span>SCREENSHOT & UPLOAD</span>
              </>
            )}
          </Button>
        </header>

        {/* Main Content Grid */}
        <main className="main-grid">
          {/* Notes Input - Single Line at Top */}
          <div className="card card-text" data-testid="text-input-card">
            <div className="card-label">
              <Type size={14} />
              <span>NOTES</span>
            </div>
            <Input
              type="text"
              placeholder="Wave start details"
              value={freeText}
              onChange={(e) => {
                const filtered = e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9 ]/g, '');
                setFreeText(filtered);
              }}
              className="text-input"
              data-testid="text-input"
            />
          </div>

          {/* Epoch Time Card - Large */}
          <div className="card card-epoch" data-testid="epoch-display">
            <div className="card-label">
              <Clock size={14} />
              <span>EPOCH TIME</span>
            </div>
            <div className="epoch-value" data-testid="epoch-value">
              {epochTime}
            </div>
            <div className="epoch-unit">UNIX TIMESTAMP (SECONDS)</div>
          </div>

          {/* Human Time Card */}
          <div className="card card-time" data-testid="time-display">
            <div className="card-label">
              <Globe size={14} />
              <span>LOCAL TIME</span>
            </div>
            <div className="time-value" data-testid="time-value">
              {getTimeOnly()}
            </div>
            <div className="date-value">{getDateOnly()}</div>
          </div>

          {/* Timezone Selector Card */}
          <div className="card card-timezone" data-testid="timezone-selector">
            <div className="card-label">
              <Globe size={14} />
              <span>TIMEZONE</span>
            </div>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger
                className="timezone-select"
                data-testid="timezone-trigger"
              >
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="timezone-content">
                {TIMEZONES.map((tz) => (
                  <SelectItem
                    key={tz.value}
                    value={tz.value}
                    data-testid={`tz-${tz.value}`}
                  >
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </main>

        {/* Footer with branding */}
        <footer className="footer">
          <span>racetime.sg</span>
          <span className="footer-divider">|</span>
          <span>Race Timing Solutions</span>
        </footer>
      </div>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  if (!isAuthenticated) {
    return <PinLogin onSuccess={() => setIsAuthenticated(true)} />;
  }

  return <EpochClock />;
}

export default App;
